<script setup>
import { ref, computed } from 'vue'
import PriceDisplay from './PriceDisplay.vue'
import ProductQuantityChanger from './ProductQuantityChanger.vue'

const props = defineProps({
  line: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['changeQuantity', 'removeLine'])
const quantity = ref(props.line.quantity)

const updateQuantity = (newQuantity) => {
  emit('changeQuantity', props.line.id, newQuantity)
}

const removeLine = () => {
  emit('removeLine', props.line.id)
}

const touchStartX = ref(0)
const touchEndX = ref(0)
const isSwiping = ref(false)
const SWIPE_THRESHOLD = 100 // Minimum distance to trigger delete
const MIN_SWIPE_DISTANCE = 10 // Minimum distance to consider it a swipe and not a tap

const handleTouchStart = (event) => {
  touchStartX.value = event.touches[0].clientX
  touchEndX.value = touchStartX.value
  isSwiping.value = false
}

const handleTouchMove = (event) => {
  touchEndX.value = event.touches[0].clientX

  if (touchEndX.value < touchStartX.value) {
    isSwiping.value = true
  }
}

const handleTouchEnd = () => {
  const swipeDistance = touchStartX.value - touchEndX.value

  // Only trigger remove if swipe is leftward (from right to left), beyond SWIPE_THRESHOLD, and exceeds minimum swipe distance
  if (
    swipeDistance > MIN_SWIPE_DISTANCE &&
    swipeDistance > SWIPE_THRESHOLD &&
    touchEndX.value < touchStartX.value
  ) {
    removeLine()
  } else {
    resetSwipe()
  }
  isSwiping.value = false
}

const resetSwipe = () => {
  touchStartX.value = 0
  touchEndX.value = 0
}

const swipeDistanceStyle = computed(() => {
  const distance = touchStartX.value - touchEndX.value
  return distance > 0 ? `translateX(-${Math.min(distance, SWIPE_THRESHOLD)}px)` : 'translateX(0)'
})
</script>

<template>
  <div class="cart-line__wrapper" :class="{ swiping: isSwiping }">
    <div
      class="cart-line"
      :style="{ transform: swipeDistanceStyle }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <div class="cart-line__image">
        <img v-if="line.merchandise.media" :src="line.merchandise.media.references.edges[0].node.previewImage.url" :alt="line.merchandise.media.references.edges[0].node.previewImage.altText" />
        <img v-else :src="line.merchandise.image.src" alt="" />
      </div>
      <div class="cart-line__product">
        <span class="cart-line__product-title">{{ line.merchandise.product.title }}</span>
        <span class="cart-line__merchandise-title">{{ line.merchandise.title }}</span>
        <PriceDisplay :price="line.merchandise.price" :compareAtPrice="line.merchandise.compareAtPrice" :font-weight="700" />
        <div class="cart-line__actions">
          <ProductQuantityChanger
            v-model="quantity"
            :key="line.quantity"
            :showTrash="true"
            @update:modelValue="updateQuantity"
            @removeLine="removeLine"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cart-line {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 15px 0;
  /*background-color: var(--clr-white);*/
  position: relative;
  border-radius: 0 24px 24px 0;
  overflow: hidden;
  transition: transform 0.3s ease;

  &__wrapper {
    position: relative;

    &:after {
      content: 'Entfernen';
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--clr-white);
      font-size: 14px;
      font-weight: 700;
      opacity: 0;
      transition: opacity 0.3s;
      z-index: -1;
    }

    &:before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: -20px;
      background-color: var(--clr-red-500);
      opacity: 0;
      transition: opacity 0.3s;
      z-index: -1;
    }

    &.swiping {
      &:before,
      &:after {
        opacity: 1;
      }
    }
  }

  &__image {
    width: 140px;
    height: 140px;
    border-radius: 24px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__product {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__product-title {
    font-weight: 700;
    margin-bottom: 4px;
  }

  &__merchandise-title {
    font-size: 14px;
    margin-bottom: 12px;
    display: none; /* TEMP */
  }

  &:deep(.price) {
    margin-bottom: 12px;
  }

  &__actions {
    margin-top: auto;
  }
}
</style>
